import { forwardRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Baby, Clock3, HeartPulse, Phone, Plus, ShieldAlert, ShieldCheck, ShieldQuestion, Stethoscope } from 'lucide-react';
import { get } from '@/lib/api';
import { tr } from '@/lib/i18n-core';
import { cn, dateTime, humanize, timeOnly } from '@/lib/utils';
import { Badge, Button, StatusBadge } from '@/components/ui/primitives';
import { PatientAvatar } from '@/components/PatientAvatar';
import type { Encounter } from './types';
import { nextVitalsAreRecheck, vitalReadings } from './vitals';
import { VitalTile } from './VitalTile';
import { ageInDays, birthWeightText, gestationText, newbornFactsOf, type NewbornFacts } from './completion';

function NewbornFactsStrip({ facts }: { facts: NewbornFacts }) {
  const weight = birthWeightText(facts.birthWeightGrams);
  const gestation = gestationText(facts.gestationalAgeWeeks, facts.gestationalAgeDays);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Baby className="h-3 w-3 text-info" aria-hidden />
      <span className="font-medium text-foreground/80">{tr('opd', 'emr_at_birth')}</span>
      {weight && <span>{weight}</span>}
      {gestation && <span>· {gestation}</span>}
      {(!weight || !gestation) && (
        <Link to={`/patients/${facts.patientId}`} className="font-medium text-primary underline underline-offset-2">
          {tr('opd', 'emr_birth_details_missing')}
        </Link>
      )}
    </span>
  );
}

interface Problem {
  id: string;
  icdCode: string;
  icdTitle: string;
  status: string;
}

export function ageText(dob: string | null, approximateYears: number | null): string | null {
  if (dob) {
    const born = new Date(dob);
    const now = new Date();
    let months = (now.getFullYear() - born.getFullYear()) * 12 + (now.getMonth() - born.getMonth());
    if (now.getDate() < born.getDate()) months -= 1;
    if (months >= 24) return tr('opd', 'emr_age_years', { n: Math.floor(months / 12) });
    const days = ageInDays(dob);
    if (months < 1 && days !== null) return tr('opd', 'emr_age_days', { n: Math.max(0, days) });
    return tr('opd', 'emr_age_months', { n: Math.max(0, months) });
  }
  if (approximateYears !== null && approximateYears !== undefined) return tr('opd', 'emr_age_years_approx', { n: approximateYears });
  return null;
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function AllergyStrip({ encounter }: { encounter: Encounter }) {
  const { allergies, allergyStatus } = encounter.patient;
  if (allergies.length > 0) {
    return (
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-destructive">
          <ShieldAlert className="h-3.5 w-3.5" aria-hidden /> {tr('opd', 'allergies')}
        </span>
        {allergies.map((allergy) => (
          <span
            key={allergy.id}
            className="rounded-full border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive"
            title={allergy.reaction ?? undefined}
          >
            {allergy.substance}
            {allergy.severity === 'severe' && ' !'}
          </span>
        ))}
      </div>
    );
  }
  if (allergyStatus === 'nka') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-success">
        <ShieldCheck className="h-3.5 w-3.5" aria-hidden /> {tr('opd', 'no_known_allergies')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-warning">
      <ShieldQuestion className="h-3.5 w-3.5" aria-hidden /> {tr('opd', 'emr_allergies_not_recorded')}
    </span>
  );
}

function ProblemStrip({ patientId }: { patientId: string }) {
  const problems = useQuery({ queryKey: ['problems', patientId], queryFn: () => get<Problem[]>(`/patients/${patientId}/problems`) });
  const active = problems.data?.filter((problem) => problem.status === 'active') ?? [];
  if (active.length === 0) return null;
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{tr('opd', 'emr_active_problems')}</span>
      {active.slice(0, 4).map((problem) => (
        <Badge key={problem.id} variant="secondary" title={problem.icdCode}>
          {problem.icdTitle}
        </Badge>
      ))}
      {active.length > 4 && <span className="text-xs text-muted-foreground">+{active.length - 4}</span>}
    </div>
  );
}

export const EmrPatientBar = forwardRef<
  HTMLDivElement,
  { encounter: Encounter; actions: ReactNode; progress?: ReactNode; onRecordVitals?: () => void; onOpenVitals: () => void }
>(function EmrPatientBar({ encounter, actions, progress, onRecordVitals, onOpenVitals }, ref) {
  const { patient } = encounter;
  const closed = encounter.status === 'closed';
  const latest = encounter.vitals[0];
  const readings = vitalReadings(latest);
  const age = ageText(patient.dob, patient.approximateAgeYears);
  const newborn = newbornFactsOf(patient);
  const facts = [age, patient.sex ? humanize(patient.sex) : null, patient.bloodGroup && patient.bloodGroup !== 'unknown' ? humanize(patient.bloodGroup).replace(' pos', '+').replace(' neg', '−').toUpperCase() : null].filter(Boolean);

  return (
    <div ref={ref} className="sticky top-[var(--emr-bar-top,0px)] z-20 -mx-4 -mt-4 mb-1 bg-background no-print lg:-mx-6 lg:-mt-6">
      <div className="bg-muted/30 px-4 pb-3 pt-4 lg:px-6 lg:pt-6">
        <div className="overflow-hidden rounded-2xl border bg-card shadow-[0_8px_24px_-12px_rgb(15_23_42/0.25)] dark:shadow-[0_8px_24px_-12px_rgb(0_0_0/0.7)]">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 bg-gradient-to-r from-primary/[0.07] via-transparent to-transparent px-4 py-3">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <PatientAvatar
                name={patient.fullName}
                photoKey={patient.photoUrl}
                className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary text-base font-semibold text-primary-foreground shadow-md shadow-primary/25"
                fallback={initialsOf(patient.fullName)}
              />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="truncate text-lg font-semibold leading-tight">{patient.fullName}</h1>
                  <span className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">{patient.mrn}</span>
                  {encounter.appointment?.payerId && <Badge variant="info">{tr('opd', 'emr_insured')}</Badge>}
                  {closed ? (
                    <StatusBadge status="closed" tone="success" label={tr('opd', 'closed')} />
                  ) : (
                    <StatusBadge status="in_progress" tone="warning" label={tr('opd', 'emr_in_progress')} />
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {facts.length > 0 && <span className="font-medium text-foreground/80">{facts.join(' · ')}</span>}
                  {newborn && <NewbornFactsStrip facts={newborn} />}
                  {patient.phone && (
                    <span className="inline-flex items-center gap-1" dir="ltr">
                      <Phone className="h-3 w-3" aria-hidden /> {patient.phone}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <Stethoscope className="h-3 w-3" aria-hidden /> {encounter.doctor.name}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock3 className="h-3 w-3" aria-hidden /> {dateTime(encounter.startedAt)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {progress}
              {actions}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t px-4 py-2">
            <AllergyStrip encounter={encounter} />
            <ProblemStrip patientId={patient.id} />
          </div>

          <div className="flex items-stretch gap-2 overflow-x-auto border-t bg-muted/20 px-3 py-2.5">
            {latest ? (
              <>
                {readings.map((reading) => (
                  <button key={reading.key} type="button" onClick={onOpenVitals} className="shrink-0 rounded-xl text-start focus-ring">
                    <VitalTile reading={reading} size="sm" />
                  </button>
                ))}
                <span className="ms-auto flex shrink-0 flex-col justify-center ps-2 text-end text-[11px] leading-tight text-muted-foreground">
                  <span>{latest.isRecheck ? tr('opd', 'emr_vitals_rechecked') : tr('opd', 'emr_vitals_taken')}</span>
                  <span className="font-medium text-foreground/80">{timeOnly(latest.recordedAt)}</span>
                </span>
              </>
            ) : (
              <div className="flex flex-1 items-center gap-2 px-1 text-sm text-muted-foreground">
                <HeartPulse className="h-4 w-4" aria-hidden /> {tr('opd', 'emr_no_vitals_yet')}
              </div>
            )}
            {onRecordVitals && (
              <Button
                variant="outline"
                size="sm"
                className={cn('shrink-0 self-center', latest && 'ms-1')}
                onClick={onRecordVitals}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden /> {nextVitalsAreRecheck(encounter) ? tr('opd', 'vitals_recheck') : tr('opd', 'record_vitals')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
